import { getRuleBranches } from '@/entities/taxonomy';
import { get } from './lookup';
import type {
  TaxonomyField,
  TaxonomyFieldValue,
  TaxonomyRule,
  TaxonomyRuleConditionGroup,
} from '@/entities/taxonomy';
import type { TaxonomyRecordCells } from '@/entities/taxonomy-dataset';
import type { ResolvedList } from '@/entities/taxonomy-list';

/**
 * What a schema's rules say about one record, once its current values are known.
 *
 * Rules have been storable since the schema builder shipped and enforced
 * nowhere: `taxonomy-rule.types.ts` says outright that "enforcement at
 * name-generation time is a later concern". This is that concern. Without it a
 * grid happily offers values the schema's own governance forbids, which is the
 * failure the whole module exists to prevent.
 *
 * Evaluated PER RECORD, because every action depends on that record's own
 * values — the same rule hides a column in one row and not the next.
 */
export interface EffectiveField {
  /** Options this cell may offer, already narrowed by `limit_options`. */
  options: TaxonomyFieldValue[];
  /** `show_field` hid it for this record. */
  hidden: boolean;
  /** `require_field`, or the field's own `required`. */
  required: boolean;
  /** `set_default`, applied only to a cell with no value yet. */
  suggestedDefault?: string;
}

export type EffectiveFields = Record<string, EffectiveField>;

/** The value a cell currently holds, as a comparable scalar. */
function cellValue(cells: TaxonomyRecordCells, fieldId: string): string {
  const raw = cells[fieldId];
  if (Array.isArray(raw)) {
    return raw[0] ?? '';
  }
  return raw ?? '';
}

/**
 * Whether a condition group holds for this record.
 *
 * `and` requires every leaf AND every nested group; `or` requires any. An EMPTY
 * group is vacuously true, which matters: a branch whose conditions were
 * cleared should not silently start firing on nothing, so callers must treat an
 * empty `when` as "always" deliberately rather than by accident — that is what
 * the schema editor writes for an unconditional branch.
 */
function groupHolds(group: TaxonomyRuleConditionGroup, cells: TaxonomyRecordCells): boolean {
  const leaves = group.conditions.map((condition) => {
    const actual = cellValue(cells, condition.fieldId);
    return condition.operator === 'is_not'
      ? actual !== condition.value
      : actual === condition.value;
  });
  const nested = group.groups.map((g) => groupHolds(g, cells));
  const all = [...leaves, ...nested];

  if (all.length === 0) {
    return true;
  }
  return group.combinator === 'or' ? all.some(Boolean) : all.every(Boolean);
}

/**
 * The starting options for a field: the resolved list when it is bound, its own
 * inline vocabulary otherwise.
 *
 * A bound field's `values` is a stale private copy — the list is authoritative —
 * which is the same precedence `assembleOutput` and the mint apply. Getting it
 * backwards offers an option the generator cannot find.
 */
function baseOptions(
  field: TaxonomyField,
  resolvedLists: Record<string, ResolvedList>,
): TaxonomyFieldValue[] {
  return usableOptions(vocabularySource(field, resolvedLists));
}

/** The raw vocabulary `baseOptions` draws from, before blanks and repeats are removed. */
export function vocabularySource(
  field: TaxonomyField,
  resolvedLists: Record<string, ResolvedList>,
): readonly TaxonomyFieldValue[] {
  const resolved = get(resolvedLists, field.id);
  if (field.listRef?.listId && resolved && resolved.values.length > 0) {
    return resolved.values;
  }
  return field.values;
}

/**
 * An inline vocabulary, with the entries a cell cannot render removed.
 *
 * The schema builder permits publishing a field whose values include blanks or
 * repeats, and so does a shared list: its rows are `jsonb` with no constraint,
 * `setVersionStatus` checks only the status transition on publish, and
 * `validateSchema` skips a bound field's values entirely. `resolveFromList`
 * trims and drops blanks but does NOT dedupe, so BOTH paths run through here.
 * Two consequences, both observed:
 *
 *  - The picker keys each option by its value, so two blanks collide and React
 *    reuses one DOM node for two rows. In a grid that is not a warning; it is a
 *    value appearing against the wrong record.
 *  - An empty value is reserved by the picker for CLEARING, so a blank option
 *    would render an item that silently means "unset".
 *
 * Deduped on the value rather than the whole entry, because the value is what
 * both the key and the stored cell are drawn from; the first spelling of a
 * repeated value wins.
 */
export function usableOptions(values: readonly TaxonomyFieldValue[]): TaxonomyFieldValue[] {
  const seen = new Set<string>();
  return values.filter((option) => {
    const value = option.value.trim();
    if (!value || seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
}

/**
 * Applies every rule branch that fires for this record.
 *
 * Order-independent by construction: `limit_options` intersects, `require_field`
 * and `set_default` are idempotent, and `show_field` is resolved from the whole
 * set of branches targeting a field rather than the last one seen.
 */
export function applyRules(
  fields: readonly TaxonomyField[],
  rules: readonly TaxonomyRule[],
  cells: TaxonomyRecordCells,
  resolvedLists: Record<string, ResolvedList>,
): EffectiveFields {
  const effective: EffectiveFields = {};
  for (const field of fields) {
    effective[field.id] = {
      options: baseOptions(field, resolvedLists),
      hidden: false,
      required: field.required,
    };
  }

  /**
   * `show_field` needs both halves before it can decide: a field targeted by
   * one is hidden UNLESS some branch targeting it fires. Tallying visibility in
   * the same pass as the other actions got it wrong, because the first
   * non-firing branch would hide a field a later firing branch reveals.
   */
  const showTargets = new Map<string, boolean>();

  for (const rule of rules) {
    for (const branch of getRuleBranches(rule)) {
      const target = get(effective, branch.targetFieldId);
      if (!target) {
        // The rule points at a column that no longer exists. Validate reports
        // it as `rule_dead_field`; here it simply cannot apply.
        continue;
      }

      const fires = groupHolds(branch.when, cells);

      if (branch.action === 'show_field') {
        showTargets.set(
          branch.targetFieldId,
          (showTargets.get(branch.targetFieldId) ?? false) || fires,
        );
        continue;
      }
      if (!fires) {
        continue;
      }

      switch (branch.action) {
        case 'limit_options': {
          // INTERSECTS rather than replaces, so two firing branches narrow
          // instead of the second undoing the first — a widening rule would
          // make the pair order-dependent in a way nobody authoring them
          // would expect.
          const allowed = new Set(branch.values ?? []);
          target.options = target.options.filter((o) => allowed.has(o.value));
          break;
        }
        case 'set_default':
          if (branch.defaultValue) {
            target.suggestedDefault = branch.defaultValue;
          }
          break;
        case 'require_field':
          target.required = true;
          break;
      }
    }
  }

  for (const [fieldId, shown] of showTargets) {
    const target = get(effective, fieldId);
    if (target) {
      target.hidden = !shown;
    }
  }

  return effective;
}
